import type { ValidationRequest, ValidationResponse, ValidationError, KubernetesManifest } from '../types';
import { getTaskById } from '../tasks';
import { parseYaml } from '../utils/yaml';
import { validateYamlFormat, suggestFixes } from '../utils/yaml-normalize';
import { SmartValidator } from './smart-validator';

export class KubernetesValidator extends SmartValidator {
  public async validateManifest(request: ValidationRequest): Promise<ValidationResponse> {
    try {
      // Получаем задание по ID
      const task = getTaskById(request.taskId);
      if (!task) {
        return {
          success: false,
          taskId: request.taskId,
          errors: [],
          message: `Задание с ID ${request.taskId} не найдено`
        };
      }

      // Проверяем формат YAML и даем советы по улучшению
      const formatWarnings = validateYamlFormat(request.yaml);
      const yamlSuggestions = suggestFixes(request.yaml);
      
      // Парсим YAML
      let manifests: KubernetesManifest[];
      try {
        manifests = parseYaml(request.yaml);
      } catch (error) {
        let errorMessage = `Ошибка парсинга YAML: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`;
        
        // Добавляем советы по исправлению
        if (yamlSuggestions.length > 0) {
          errorMessage += '\n\n📝 Предложения по исправлению:\n' + yamlSuggestions.join('\n');
        }
        
        return {
          success: false,
          taskId: request.taskId,
          errors: [],
          message: errorMessage
        };
      }

      if (manifests.length === 0) {
        return {
          success: false,
          taskId: request.taskId,
          errors: [],
          message: 'YAML не содержит валидных Kubernetes манифестов'
        };
      }

      // Выполняем валидацию согласно правилам задания
      const errors = this.validate(manifests, task.validationRules);

      const success = errors.length === 0;
      
      // Формируем сообщение с учетом предупреждений о форматировании
      let successMessage = `Отлично! Задание "${task.name}" выполнено успешно! ${task.reward}`;
      let errorMessage = `Найдены ошибки в манифесте. Проверьте исправления.`;
      
      // Добавляем предупреждения о форматировании
      if (formatWarnings.length > 0 && success) {
        successMessage += '\n\n⚠️ Замечания по оформлению:\n' + formatWarnings.join('\n');
        successMessage += '\n\n💡 Ваше решение работает, но можно улучшить читаемость кода!';
      }
      
      if (yamlSuggestions.length > 0 && !success) {
        errorMessage += '\n\n📝 Дополнительные советы:\n' + yamlSuggestions.join('\n');
      }
      
      return {
        success,
        taskId: request.taskId,
        errors,
        message: success ? successMessage : errorMessage,
        hint: success ? undefined : task.hint
      };

    } catch (error) {
      return {
        success: false,
        taskId: request.taskId,
        errors: [],
        message: `Внутренняя ошибка валидатора: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`
      };
    }
  }

  // Специализированные методы валидации для разных типов заданий
  private validateDeploymentReplicas(manifests: KubernetesManifest[]): ValidationError[] {
    const deployment = this.findManifestByKind(manifests, 'Deployment');
    if (!deployment) {
      return [{
        path: 'kind',
        expected: 'Deployment',
        actual: 'отсутствует',
        message: 'Манифест должен содержать Deployment'
      }];
    }

    const errors: ValidationError[] = [];
    const spec = deployment.spec;

    // Проверяем количество реплик
    if (!spec.replicas || spec.replicas < 2) {
      errors.push({
        path: 'spec.replicas',
        expected: 'больше 1',
        actual: spec.replicas || 'отсутствует',
        message: 'Deployment должен иметь минимум 2 реплики для обеспечения высокой доступности'
      });
    }

    // Проверяем стратегию обновления
    if (spec.strategy?.type === 'Recreate') {
      errors.push({
        path: 'spec.strategy.type',
        expected: 'RollingUpdate или отсутствует',
        actual: 'Recreate',
        message: 'Стратегия Recreate вызывает даунтайм. Используйте RollingUpdate для плавного обновления'
      });
    }

    return errors;
  }

  private validateSecrets(manifests: KubernetesManifest[]): ValidationError[] {
    // Этот метод больше не используется, валидация идет через SmartValidator
    return [];
  }

  private validateProbes(manifests: KubernetesManifest[]): ValidationError[] {
    const deployment = this.findManifestByKind(manifests, 'Deployment');
    if (!deployment) {
      return [{
        path: 'kind',
        expected: 'Deployment',
        actual: 'отсутствует',
        message: 'Манифест должен содержать Deployment'
      }];
    }

    const errors: ValidationError[] = [];
    const container = deployment.spec.template.spec.containers[0];

    if (!container) {
      return [{
        path: 'spec.template.spec.containers',
        expected: 'минимум один контейнер',
        actual: 'отсутствует',
        message: 'Pod должен содержать минимум один контейнер'
      }];
    }

    // Проверяем livenessProbe
    if (!container.livenessProbe) {
      errors.push({
        path: 'spec.template.spec.containers[0].livenessProbe',
        expected: 'конфигурация пробы',
        actual: 'отсутствует',
        message: 'Контейнер должен иметь livenessProbe для проверки жизнеспособности'
      });
    } else {
      if (container.livenessProbe.httpGet?.path !== '/healthz') {
        errors.push({
          path: 'spec.template.spec.containers[0].livenessProbe.httpGet.path',
          expected: '/healthz',
          actual: container.livenessProbe.httpGet?.path || 'отсутствует',
          message: 'livenessProbe должен проверять путь /healthz'
        });
      }
      if (container.livenessProbe.httpGet?.port !== 8080) {
        errors.push({
          path: 'spec.template.spec.containers[0].livenessProbe.httpGet.port',
          expected: 8080,
          actual: container.livenessProbe.httpGet?.port || 'отсутствует',
          message: 'livenessProbe должен использовать порт 8080'
        });
      }
    }

    // Проверяем readinessProbe
    if (!container.readinessProbe) {
      errors.push({
        path: 'spec.template.spec.containers[0].readinessProbe',
        expected: 'конфигурация пробы',
        actual: 'отсутствует',
        message: 'Контейнер должен иметь readinessProbe для проверки готовности'
      });
    } else {
      if (container.readinessProbe.httpGet?.path !== '/healthz') {
        errors.push({
          path: 'spec.template.spec.containers[0].readinessProbe.httpGet.path',
          expected: '/healthz',
          actual: container.readinessProbe.httpGet?.path || 'отсутствует',
          message: 'readinessProbe должен проверять путь /healthz'
        });
      }
      if (container.readinessProbe.httpGet?.port !== 8080) {
        errors.push({
          path: 'spec.template.spec.containers[0].readinessProbe.httpGet.port',
          expected: 8080,
          actual: container.readinessProbe.httpGet?.port || 'отсутствует',
          message: 'readinessProbe должен использовать порт 8080'
        });
      }
    }

    return errors;
  }
}

// Глобальный экземпляр валидатора
export const validator = new KubernetesValidator();
